import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useAgenda, AgendaItem } from '@/hooks/useAgenda';
import { format, parseISO, isToday } from 'date-fns';
import { nl } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, X, Trash2, ArrowUpRight } from 'lucide-react';

// ── helpers ────────────────────────────────────────────────────────────────────
function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

function getMondayOf(date: Date): string {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().split('T')[0];
}

function weekLabel(mondayStr: string): string {
  const mon = parseISO(mondayStr);
  const sun = parseISO(addDays(mondayStr, 6));
  return `${format(mon, 'd', { locale: nl })} – ${format(sun, 'd MMM yyyy', { locale: nl })}`;
}

const DAY_ABBR = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];

// ── type config ────────────────────────────────────────────────────────────────
const TYPE_CFG: Record<string, { label: string; bg: string; border: string; text: string; dot: string }> = {
  opmeetbezoek: { label: 'Opmeetbezoek', bg: 'rgba(168,152,128,0.18)', border: '#A89880', text: '#7A6855', dot: '#A89880' },
  installatie:  { label: 'Installatie',  bg: 'rgba(122,140,110,0.18)', border: '#7A8C6E', text: '#4A6040', dot: '#7A8C6E' },
  geblokkeerd:  { label: 'Geblokkeerd',  bg: 'rgba(74,69,64,0.08)',    border: 'var(--border-primary)', text: 'var(--text-muted)', dot: 'var(--text-muted)' },
  showroom:     { label: 'Showroom',     bg: 'rgba(107,143,171,0.18)', border: '#6B8FAB', text: '#3D6B8A', dot: '#6B8FAB' },
  werkbezoek:   { label: 'Werkbezoek',   bg: 'rgba(138,126,154,0.15)', border: '#8A7E9A', text: '#6B5E7A', dot: '#8A7E9A' },
  anders:       { label: 'Overig',       bg: 'var(--bg-input)',         border: 'var(--border-primary)', text: 'var(--text-secondary)', dot: 'var(--text-muted)' },
};

type FormState = {
  name: string; date: string; end_date: string;
  time: string; type: AgendaItem['type']; notes: string;
};

const EMPTY_FORM: FormState = { name: '', date: '', end_date: '', time: '', type: 'showroom', notes: '' };

const isMultiDayType = (t: string) => t === 'installatie' || t === 'geblokkeerd';

// ── component ─────────────────────────────────────────────────────────────────
export default function PartnerAgendaPage() {
  const navigate = useNavigate();
  const { effectivePartnerId } = useAuth();
  const { items, loading, addItem, deleteItem } = useAgenda(effectivePartnerId);

  const [weekStart, setWeekStart] = useState(() => getMondayOf(new Date()));
  const [selectedItem, setSelectedItem] = useState<AgendaItem | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [blocking, setBlocking] = useState(false);

  const todayStr = new Date().toISOString().split('T')[0];
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const weekEnd = weekDays[6];

  // Items that overlap any day of this week
  const weekItems = useMemo(() => items.filter(item => {
    const iEnd = item.end_date || item.date;
    return item.date <= weekEnd && iEnd >= weekStart;
  }), [items, weekStart, weekEnd]);

  const spanningItems = useMemo(
    () => weekItems.filter(i => i.end_date && i.end_date > i.date),
    [weekItems],
  );

  const getItemsForDay = useCallback((dayStr: string) =>
    weekItems.filter(i => (!i.end_date || i.end_date === i.date) && i.date === dayStr),
    [weekItems],
  );

  const getSpanRange = (item: AgendaItem) => {
    const clampedStart = item.date < weekStart ? weekStart : item.date;
    const clampedEnd = (item.end_date || item.date) > weekEnd ? weekEnd : (item.end_date || item.date);
    const s = weekDays.indexOf(clampedStart) + 1;
    const e = weekDays.indexOf(clampedEnd) + 2;
    return { start: Math.max(1, s), end: Math.min(8, e) };
  };

  const weekInstallaties = weekItems.filter(i => i.type === 'installatie').length;
  const weekOpmeet = weekItems.filter(i => i.type === 'opmeetbezoek').length;
  const MAX_INSTALL = 2;

  const openAdd = (date?: string) => {
    setForm({ ...EMPTY_FORM, date: date || weekStart });
    setShowAdd(true);
  };

  const handleAdd = async () => {
    if (!effectivePartnerId || !form.date) return;
    setSaving(true);
    const multiDay = isMultiDayType(form.type);
    await addItem({
      partner_id: effectivePartnerId,
      lead_id: null,
      type: form.type,
      name: form.name || TYPE_CFG[form.type]?.label || form.type,
      date: form.date,
      end_date: multiDay && form.end_date ? form.end_date : undefined,
      all_day: multiDay,
      color: form.type === 'installatie' ? '#7A8C6E' : form.type === 'opmeetbezoek' ? '#A89880' : undefined,
      time: form.time,
      notes: form.notes,
    });
    setSaving(false);
    setShowAdd(false);
    setForm(EMPTY_FORM);
  };

  const handleBlockWeek = async () => {
    if (!effectivePartnerId) return;
    setBlocking(true);
    await addItem({
      partner_id: effectivePartnerId,
      lead_id: null,
      type: 'geblokkeerd',
      name: 'Week geblokkeerd',
      date: weekStart,
      end_date: weekEnd,
      all_day: true,
      color: undefined,
      time: '',
      notes: '',
    });
    setBlocking(false);
  };

  const s = { background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' } as const;
  const lbl = { color: 'var(--text-muted)', fontSize: '10px', fontWeight: 600 as const, letterSpacing: '0.08em', textTransform: 'uppercase' as const };

  return (
    <div className="flex flex-col lg:flex-row gap-4" style={{ minHeight: 0 }}>

      {/* ── Main calendar ─────────────────────────────────────────────────────── */}
      <div className="flex-1 min-w-0 space-y-4">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold tracking-tight" style={{ color: 'var(--text-primary)' }}>Agenda</h1>
            <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>{weekLabel(weekStart)}</p>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setWeekStart(getMondayOf(new Date()))}
              className="px-3 py-1.5 rounded-lg text-xs font-medium border transition-all"
              style={{ background: 'var(--bg-input)', color: 'var(--text-secondary)', borderColor: 'var(--border-primary)' }}
            >
              Vandaag
            </button>
            <button onClick={() => setWeekStart(addDays(weekStart, -7))} aria-label="Vorige week" className="p-1.5 rounded-lg border transition-all" style={{ background: 'var(--bg-input)', color: 'var(--text-secondary)', borderColor: 'var(--border-primary)' }}>
              <ChevronLeft size={16} />
            </button>
            <button onClick={() => setWeekStart(addDays(weekStart, 7))} aria-label="Volgende week" className="p-1.5 rounded-lg border transition-all" style={{ background: 'var(--bg-input)', color: 'var(--text-secondary)', borderColor: 'var(--border-primary)' }}>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 rounded-full border-2 animate-spin" style={{ borderColor: 'var(--border-primary)', borderTopColor: 'var(--accent)' }} />
          </div>
        ) : (
          <div className="overflow-x-auto -mx-1 px-1">
          <div className="rounded-xl border overflow-hidden" style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)', minWidth: '560px' }}>

            {/* Day headers */}
            <div className="grid grid-cols-7" style={{ borderBottom: '1px solid var(--border-separator)' }}>
              {weekDays.map((day, i) => {
                const isNow = day === todayStr;
                const d = parseISO(day);
                return (
                  <div key={day} className="px-2 py-3 text-center border-r last:border-r-0" style={{ borderColor: 'var(--border-separator)' }}>
                    <p className="text-[10px] uppercase font-semibold mb-1" style={{ color: isNow ? 'var(--accent)' : 'var(--text-muted)' }}>
                      {DAY_ABBR[i]}
                    </p>
                    <div className="w-7 h-7 rounded-full mx-auto flex items-center justify-center text-sm font-semibold"
                      style={isNow ? { background: 'var(--accent)', color: '#fff' } : { color: 'var(--text-secondary)' }}>
                      {format(d, 'd')}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* All-day / spanning items zone */}
            {spanningItems.length > 0 && (
              <div className="grid grid-cols-7 px-1 py-1 gap-y-1" style={{ borderBottom: '1px solid var(--border-separator)', gridAutoRows: '22px' }}>
                {spanningItems.map(item => {
                  const { start, end } = getSpanRange(item);
                  const cfg = TYPE_CFG[item.type] || TYPE_CFG.anders;
                  return (
                    <button
                      key={item.id}
                      onClick={() => setSelectedItem(item)}
                      className="rounded text-left px-2 text-[11px] font-medium truncate transition-opacity hover:opacity-80"
                      style={{
                        gridColumn: `${start} / ${end}`,
                        background: cfg.bg,
                        border: `1px solid ${cfg.border}`,
                        color: cfg.text,
                        fontStyle: item.type === 'geblokkeerd' ? 'italic' : 'normal',
                      }}
                    >
                      {item.type === 'geblokkeerd' ? 'Geblokkeerd' : item.name}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Day columns */}
            <div className="grid grid-cols-7" style={{ minHeight: 280 }}>
              {weekDays.map(day => {
                const dayItems = getItemsForDay(day);
                return (
                  <div key={day} className="border-r last:border-r-0 p-1.5 flex flex-col gap-1" style={{ borderColor: 'var(--border-separator)', background: day === todayStr ? 'rgba(59,130,246,0.04)' : undefined }}>
                    {dayItems.map(item => {
                      const cfg = TYPE_CFG[item.type] || TYPE_CFG.anders;
                      return (
                        <button
                          key={item.id}
                          onClick={() => setSelectedItem(item)}
                          className="w-full text-left rounded px-1.5 py-1 leading-tight transition-opacity hover:opacity-80"
                          style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, color: cfg.text, fontSize: 11, fontStyle: item.type === 'geblokkeerd' ? 'italic' : 'normal' }}
                        >
                          {item.time && <span style={{ opacity: 0.65 }}>{item.time} </span>}
                          <span className="truncate block">{item.name}</span>
                        </button>
                      );
                    })}
                    <button
                      onClick={() => openAdd(day)}
                      className="mt-auto w-full py-0.5 rounded text-[11px] opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center"
                      style={{ color: 'var(--text-muted)', border: '1px dashed var(--border-separator)' }}
                    >
                      +
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
          </div>
        )}
      </div>

      {/* ── Capacity sidebar ──────────────────────────────────────────────────── */}
      <div className="w-full lg:w-48 shrink-0 space-y-3">

        <div className="rounded-xl p-4 border space-y-3" style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}>
          <h3 className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Deze week</h3>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span style={{ color: 'var(--text-secondary)' }}>Opmeetbezoeken</span>
              <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{weekOpmeet}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span style={{ color: 'var(--text-secondary)' }}>Installaties</span>
              <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{weekInstallaties}</span>
            </div>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-wider mb-1.5 font-semibold" style={{ color: 'var(--text-muted)' }}>Capaciteit</p>
            <div className="flex gap-1.5">
              {Array.from({ length: MAX_INSTALL }, (_, i) => (
                <div key={i} className="w-3.5 h-3.5 rounded-full border"
                  style={i < weekInstallaties
                    ? { background: '#7A8C6E', borderColor: '#7A8C6E' }
                    : { background: 'transparent', borderColor: 'var(--border-primary)' }}
                />
              ))}
              <span className="text-xs ml-1" style={{ color: 'var(--text-muted)' }}>
                {weekInstallaties >= MAX_INSTALL ? 'Vol' : `${MAX_INSTALL - weekInstallaties} vrij`}
              </span>
            </div>
          </div>

          <button
            onClick={handleBlockWeek}
            disabled={blocking}
            className="w-full py-1.5 text-xs rounded-lg border transition-all disabled:opacity-40"
            style={{ color: 'var(--text-muted)', borderColor: 'var(--border-primary)', background: 'var(--bg-input)' }}
          >
            {blocking ? 'Bezig...' : 'Week blokkeren'}
          </button>
        </div>

        {/* Legend */}
        <div className="rounded-xl p-4 border space-y-2" style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}>
          <h3 className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Legenda</h3>
          {Object.entries(TYPE_CFG).map(([key, cfg]) => (
            <div key={key} className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: cfg.dot }} />
              <span className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>{cfg.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Detail modal ──────────────────────────────────────────────────────── */}
      {selectedItem && (() => {
        const item = selectedItem;
        const cfg = TYPE_CFG[item.type] || TYPE_CFG.anders;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(10px)' }}>
            <div className="rounded-xl p-6 w-full max-w-sm" style={{ background: 'var(--bg-modal)', border: '1px solid var(--border-modal)', boxShadow: 'var(--shadow-card)' }}>
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: cfg.bg, color: cfg.text, border: `1px solid ${cfg.border}` }}>
                  {cfg.label}
                </span>
                <button onClick={() => setSelectedItem(null)} aria-label="Sluiten" style={{ color: 'var(--text-muted)' }}><X size={16} /></button>
              </div>
              <h3 className="text-base font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>{item.name}</h3>
              <div className="space-y-1.5 text-sm mb-4">
                <div className="flex justify-between">
                  <span style={{ color: 'var(--text-muted)' }}>Datum</span>
                  <span style={{ color: 'var(--text-secondary)' }}>
                    {item.date}{item.end_date && item.end_date !== item.date ? ` – ${item.end_date}` : ''}
                  </span>
                </div>
                {item.time && (
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--text-muted)' }}>Tijd</span>
                    <span style={{ color: 'var(--text-secondary)' }}>{item.time}</span>
                  </div>
                )}
                {item.notes && (
                  <div className="flex justify-between gap-4">
                    <span style={{ color: 'var(--text-muted)' }}>Notities</span>
                    <span className="text-right" style={{ color: 'var(--text-secondary)' }}>{item.notes}</span>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                {item.lead_id && (
                  <button
                    onClick={() => { setSelectedItem(null); navigate(`/partner/leads/${item.lead_id}`); }}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-all"
                    style={{ color: 'var(--accent)', borderColor: 'var(--accent)', background: 'var(--accent-dim)' }}
                  >
                    <ArrowUpRight size={12} /> Bekijk lead
                  </button>
                )}
                <button
                  onClick={async () => { await deleteItem(item.id); setSelectedItem(null); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-all ml-auto"
                  style={{ color: 'var(--danger-text)', borderColor: 'var(--danger-border)', background: 'var(--danger-bg)' }}
                >
                  <Trash2 size={12} /> Verwijderen
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Add modal ─────────────────────────────────────────────────────────── */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(10px)' }}>
          <div className="rounded-xl p-6 w-full max-w-sm" style={{ background: 'var(--bg-modal)', border: '1px solid var(--border-modal)', boxShadow: 'var(--shadow-card)' }}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Afspraak toevoegen</h3>
              <button onClick={() => setShowAdd(false)} aria-label="Sluiten" style={{ color: 'var(--text-muted)' }}><X size={16} /></button>
            </div>

            <div className="space-y-3">
              <div>
                <label style={{ ...lbl, display: 'block' }} className="mb-1.5">Type</label>
                <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value as AgendaItem['type'] }))} className="w-full px-3 py-2 text-sm rounded-lg outline-none" style={s}>
                  <option value="opmeetbezoek">Opmeetbezoek</option>
                  <option value="installatie">Installatie</option>
                  <option value="geblokkeerd">Geblokkeerd</option>
                  <option value="showroom">Showroom</option>
                  <option value="werkbezoek">Werkbezoek</option>
                  <option value="anders">Overig</option>
                </select>
              </div>

              {form.type !== 'geblokkeerd' && (
                <div>
                  <label style={{ ...lbl, display: 'block' }} className="mb-1.5">Klant (optioneel)</label>
                  <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Naam klant" className="w-full px-3 py-2 text-sm rounded-lg outline-none" style={s} />
                </div>
              )}

              <div className={`grid gap-3 ${isMultiDayType(form.type) ? 'grid-cols-2' : 'grid-cols-2'}`}>
                <div>
                  <label style={{ ...lbl, display: 'block' }} className="mb-1.5">Datum</label>
                  <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="w-full px-3 py-2 text-sm rounded-lg outline-none" style={s} />
                </div>
                {isMultiDayType(form.type) ? (
                  <div>
                    <label style={{ ...lbl, display: 'block' }} className="mb-1.5">Einddatum</label>
                    <input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} className="w-full px-3 py-2 text-sm rounded-lg outline-none" style={s} />
                  </div>
                ) : (
                  <div>
                    <label style={{ ...lbl, display: 'block' }} className="mb-1.5">Tijdstip</label>
                    <input type="time" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))} className="w-full px-3 py-2 text-sm rounded-lg outline-none" style={s} />
                  </div>
                )}
              </div>

              <div>
                <label style={{ ...lbl, display: 'block' }} className="mb-1.5">Notities</label>
                <textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optionele notitie..." className="w-full px-3 py-2 text-xs rounded-lg outline-none resize-none" style={s} />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowAdd(false)} className="px-3 py-1.5 text-xs rounded-lg border" style={{ color: 'var(--text-muted)', borderColor: 'var(--border-primary)' }}>Annuleren</button>
              <button onClick={handleAdd} disabled={!form.date || saving} className="px-4 py-1.5 text-xs rounded-lg font-medium disabled:opacity-40" style={{ background: 'var(--accent)', color: '#fff' }}>
                {saving ? 'Opslaan...' : 'Opslaan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
